import {
  AuthenticationDetails,
  CognitoAccessToken,
  CognitoIdToken,
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
  CognitoUserSession,
} from 'amazon-cognito-identity-js'
import { Platform } from 'react-native'
import { getPostHogClient } from '@/hooks/useAnalytics'
import {
  E2E_SIGNIN_FAIL_ONCE_KEY,
  isE2EMockAuthEnabled,
  readE2EBooleanFlag,
  writeE2EBooleanFlag,
} from '@/lib/e2e'
import { storage } from '@/lib/storage'

const POOL_ID_ENV = process.env.EXPO_PUBLIC_COGNITO_USER_POOL_ID
const CLIENT_ID_ENV = process.env.EXPO_PUBLIC_COGNITO_CLIENT_ID
const COGNITO_DOMAIN_ENV = process.env.EXPO_PUBLIC_COGNITO_DOMAIN
const E2E_MOCK_AUTH = isE2EMockAuthEnabled()

if ((!POOL_ID_ENV || !CLIENT_ID_ENV) && !E2E_MOCK_AUTH) {
  throw new Error(
    'Missing Cognito environment variables: EXPO_PUBLIC_COGNITO_USER_POOL_ID or EXPO_PUBLIC_COGNITO_CLIENT_ID'
  )
}

const POOL_ID = POOL_ID_ENV ?? 'e2e_pool'
const CLIENT_ID = CLIENT_ID_ENV ?? 'e2e_client'

// ICognitoStorage requires synchronous getItem; expo-secure-store is async-only.
// We manage our own persistent token storage separately, so an in-memory store
// is sufficient for the SDK's session cache.
const _mem = new Map<string, string>()
const cognitoStorage = {
  setItem: (key: string, value: string) => {
    _mem.set(key, value)
  },
  getItem: (key: string) => _mem.get(key) ?? null,
  removeItem: (key: string) => {
    _mem.delete(key)
  },
  clear: () => {
    _mem.clear()
  },
}

const pool = new CognitoUserPool({
  UserPoolId: POOL_ID,
  ClientId: CLIENT_ID,
  Storage: cognitoStorage,
})

const KEYS = {
  accessToken: 'turbina_access_token',
  idToken: 'turbina_id_token',
  refreshToken: 'turbina_refresh_token',
  username: 'turbina_cognito_username',
  authMethod: 'turbina_auth_method',
} as const

const E2E_AUTH_USERS_KEY = 'turbina_e2e_auth_users'

export type AuthMethod = 'password' | 'google' | 'e2e'

type E2EAuthUser = {
  password: string
  confirmed: boolean
}

type E2EAuthError = Error & {
  code: string
  name: string
}

type TokenClaims = Record<string, unknown>

type SuccessfulTokenGrantResponse = {
  access_token: string
  id_token: string
  refresh_token?: string
}

type TokenGrantErrorResponse = {
  error?: string
  error_description?: string
}

export interface StoredTokens {
  accessToken: string
  idToken: string
  refreshToken: string
  username: string | null
  authMethod: AuthMethod
}

export type TokenProfile = {
  email: string | null
  name: string | null
  surname: string | null
  username: string | null
  sub: string | null
}

function createE2EAuthError(code: string, message: string): E2EAuthError {
  const err = new Error(message) as E2EAuthError
  err.code = code
  err.name = code
  return err
}

function readSignInFailureFlag(): boolean {
  return readE2EBooleanFlag(E2E_SIGNIN_FAIL_ONCE_KEY)
}

function consumeSignInFailureFlag(): void {
  writeE2EBooleanFlag(E2E_SIGNIN_FAIL_ONCE_KEY, false)
}

async function loadE2EUsers(): Promise<Record<string, E2EAuthUser>> {
  const raw = await storage.get(E2E_AUTH_USERS_KEY)
  if (!raw) return {}
  try {
    return JSON.parse(raw) as Record<string, E2EAuthUser>
  } catch {
    return {}
  }
}

async function saveE2EUsers(users: Record<string, E2EAuthUser>): Promise<void> {
  await storage.set(E2E_AUTH_USERS_KEY, JSON.stringify(users))
}

function base64UrlEncodeJson(payload: Record<string, unknown>): string {
  if (typeof globalThis.btoa !== 'function') {
    throw new Error('Mock auth requires btoa support')
  }
  return globalThis
    .btoa(JSON.stringify(payload))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '')
}

function sanitizeEmailToSubject(email: string): string {
  return `e2e-${email.toLowerCase().replace(/[^a-z0-9]/g, '-')}`
}

function createE2EJwt(payload: Record<string, unknown>): string {
  const header = base64UrlEncodeJson({ alg: 'none', typ: 'JWT' })
  const body = base64UrlEncodeJson(payload)
  return `${header}.${body}.e2e`
}

function normalizeStoredUsername(username: string | null | undefined): string | null {
  const trimmed = username?.trim()
  return trimmed ? trimmed : null
}

function splitName(fullName: string | null): { name: string | null; surname: string | null } {
  if (!fullName) return { name: null, surname: null }

  const parts = fullName.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return { name: null, surname: null }
  if (parts.length === 1) return { name: parts[0], surname: null }

  return {
    name: parts[0],
    surname: parts.slice(1).join(' '),
  }
}

function readStringClaim(claims: TokenClaims | null, key: string): string | null {
  if (!claims) return null
  const value = claims[key]
  return typeof value === 'string' && value.trim() ? value : null
}

export function decodeJwtPayload<T extends Record<string, unknown>>(token: string): T | null {
  try {
    const raw = token.split('.')[1]
    if (!raw) return null
    const base64 = raw.replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')
    return JSON.parse(atob(padded)) as T
  } catch {
    return null
  }
}

function extractEmail(
  idClaims: TokenClaims | null,
  accessClaims: TokenClaims | null
): string | null {
  return readStringClaim(idClaims, 'email') ?? readStringClaim(accessClaims, 'email')
}

function logMissingEmail(idClaims: TokenClaims | null, accessClaims: TokenClaims | null): void {
  console.warn('[cognito] getTokenProfile: email claim missing', {
    idTokenClaimKeys: idClaims ? Object.keys(idClaims) : null,
    accessTokenClaimKeys: accessClaims ? Object.keys(accessClaims) : null,
    idEmailRaw: idClaims?.['email'],
    idEmailVerified: idClaims?.['email_verified'],
    idTokenUse: idClaims?.['token_use'],
    idCognitoUsername: idClaims?.['cognito:username'],
    idIdentities: idClaims?.['identities'],
  })
}

function extractName(
  idClaims: TokenClaims | null,
  fullName: string | null
): { name: string | null; surname: string | null } {
  const split = splitName(fullName)
  return {
    name: readStringClaim(idClaims, 'given_name') ?? split.name ?? null,
    surname: readStringClaim(idClaims, 'family_name') ?? split.surname ?? null,
  }
}

function extractUsername(
  idClaims: TokenClaims | null,
  accessClaims: TokenClaims | null
): string | null {
  return (
    readStringClaim(accessClaims, 'username') ??
    readStringClaim(idClaims, 'cognito:username') ??
    readStringClaim(idClaims, 'email') ??
    readStringClaim(accessClaims, 'sub')
  )
}

function extractSub(idClaims: TokenClaims | null, accessClaims: TokenClaims | null): string | null {
  return readStringClaim(idClaims, 'sub') ?? readStringClaim(accessClaims, 'sub')
}

function logDecodedClaims(
  idClaims: TokenClaims | null,
  accessClaims: TokenClaims | null,
  fullName: string | null
): void {
  const id = idClaims ?? ({} as TokenClaims)
  const acc = accessClaims ?? ({} as TokenClaims)
  console.info('[cognito][token] logDecodedClaims: decoded claims', {
    hasIdClaims: !!idClaims,
    idClaimKeys: Object.keys(id),
    hasAccessClaims: !!accessClaims,
    accessClaimKeys: Object.keys(acc),
    idEmail: id['email'],
    idEmailVerified: id['email_verified'],
    idSub: id['sub'],
    idCognitoUsername: id['cognito:username'],
    idIdentities: id['identities'],
    idTokenUse: id['token_use'],
    accessEmail: acc['email'],
    accessSub: acc['sub'],
    fullName,
    givenName: id['given_name'],
    familyName: id['family_name'],
  })
  getPostHogClient()
    .then((ph) =>
      ph.capture('google_oauth_debug', {
        prefix: '[cognito][token]',
        step: 'claims_decoded',
        hasIdClaims: !!idClaims,
        hasAccessClaims: !!accessClaims,
        idEmailVerified: id['email_verified'],
        idTokenUse: id['token_use'],
      })
    )
    .catch(() => {})
}

function logExtractedProfile(profile: TokenProfile): void {
  const subPrefix = profile.sub?.substring(0, 8) + '...' ?? 'null'
  console.info('[cognito][token] logExtractedProfile: profile extracted', {
    email: profile.email,
    name: profile.name,
    surname: profile.surname,
    username: profile.username,
    subPrefix,
    hasEmail: !!profile.email,
  })
  getPostHogClient()
    .then((ph) =>
      ph.capture('google_oauth_debug', {
        prefix: '[cognito][token]',
        step: 'profile_logged',
        hasEmail: !!profile.email,
        hasName: !!profile.name,
        hasSurname: !!profile.surname,
      })
    )
    .catch(() => {})
}

export function getTokenProfile(idToken: string, accessToken: string): TokenProfile {
  console.info('[cognito][token] getTokenProfile: decoding tokens')
  getPostHogClient()
    .then((ph) =>
      ph.capture('google_oauth_debug', {
        prefix: '[cognito][token]',
        step: 'profile_decoding',
      })
    )
    .catch(() => {})
  const idClaims = decodeJwtPayload<TokenClaims>(idToken)
  const accessClaims = decodeJwtPayload<TokenClaims>(accessToken)
  const fullName = readStringClaim(idClaims, 'name')

  logDecodedClaims(idClaims, accessClaims, fullName)

  const email = extractEmail(idClaims, accessClaims)
  if (!email) {
    logMissingEmail(idClaims, accessClaims)
  }

  const { name, surname } = extractName(idClaims, fullName)

  const profile: TokenProfile = {
    email,
    name,
    surname,
    username: extractUsername(idClaims, accessClaims),
    sub: extractSub(idClaims, accessClaims),
  }

  logExtractedProfile(profile)

  return profile
}

function getTokenEndpointURL(): string {
  const normalized = COGNITO_DOMAIN_ENV?.trim()
  if (!normalized) {
    throw new Error('Missing Cognito environment variable: EXPO_PUBLIC_COGNITO_DOMAIN')
  }

  const domain =
    normalized.startsWith('https://') || normalized.startsWith('http://')
      ? normalized
      : `https://${normalized}`
  return `${domain.replace(/\/+$/, '')}/oauth2/token`
}

// eslint-disable-next-line complexity
async function requestTokenGrant(params: URLSearchParams): Promise<SuccessfulTokenGrantResponse> {
  const endpoint = getTokenEndpointURL()
  const grantType = params.get('grant_type')
  const paramKeys = Array.from(params.keys())
  console.info('[cognito][token] requestTokenGrant: sending request', {
    endpoint,
    grantType,
    paramKeys,
    clientIdPrefix: (params.get('client_id') ?? '').substring(0, 8) + '...',
    hasCode: !!params.get('code'),
    hasCodeVerifier: !!params.get('code_verifier'),
    hasRefreshToken: !!params.get('refresh_token'),
    redirectUri: params.get('redirect_uri'),
  })
  getPostHogClient()
    .then((ph) =>
      ph.capture('google_oauth_debug', {
        prefix: '[cognito][token]',
        step: 'token_request_sending',
        grantType,
        paramKeys,
        hasCode: !!params.get('code'),
        hasCodeVerifier: !!params.get('code_verifier'),
        hasRefreshToken: !!params.get('refresh_token'),
        redirectUri: params.get('redirect_uri'),
      })
    )
    .catch(() => {})

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: params.toString(),
  })

  console.info('[cognito][token] requestTokenGrant: response received', {
    status: response.status,
    statusText: response.statusText,
    ok: response.ok,
    contentType: response.headers.get('content-type'),
  })
  getPostHogClient()
    .then((ph) =>
      ph.capture('google_oauth_debug', {
        prefix: '[cognito][token]',
        step: 'token_response_received',
        status: response.status,
        ok: response.ok,
      })
    )
    .catch(() => {})

  let payload: SuccessfulTokenGrantResponse | TokenGrantErrorResponse | null = null
  try {
    payload = (await response.json()) as SuccessfulTokenGrantResponse | TokenGrantErrorResponse
  } catch (jsonErr) {
    console.error('[cognito][token] requestTokenGrant: failed to parse response JSON', {
      error: jsonErr instanceof Error ? jsonErr.message : String(jsonErr),
      status: response.status,
    })
    getPostHogClient()
      .then((ph) =>
        ph.capture('google_oauth_debug', {
          prefix: '[cognito][token]',
          step: 'token_response_json_error',
          error: jsonErr instanceof Error ? jsonErr.message : String(jsonErr),
          status: response.status,
        })
      )
      .catch(() => {})
    payload = null
  }

  if (!response.ok) {
    const description =
      payload && !('access_token' in payload)
        ? (payload.error_description ?? payload.error ?? `HTTP ${response.status}`)
        : `HTTP ${response.status}`
    console.error('[cognito][token] requestTokenGrant: token endpoint returned error', {
      status: response.status,
      description,
      errorField: payload && 'error' in payload ? payload.error : undefined,
      errorDescriptionField:
        payload && 'error_description' in payload ? payload.error_description : undefined,
    })
    getPostHogClient()
      .then((ph) =>
        ph.capture('google_oauth_debug', {
          prefix: '[cognito][token]',
          step: 'token_endpoint_error',
          status: response.status,
          description,
        })
      )
      .catch(() => {})
    throw new Error(description)
  }

  if (!payload || !('access_token' in payload) || !payload.access_token || !payload.id_token) {
    console.error('[cognito][token] requestTokenGrant: response missing required tokens', {
      hasPayload: !!payload,
      hasAccessToken: payload ? 'access_token' in payload : false,
      hasIdToken: payload ? 'id_token' in payload : false,
      responseKeys: payload ? Object.keys(payload) : [],
    })
    getPostHogClient()
      .then((ph) =>
        ph.capture('google_oauth_debug', {
          prefix: '[cognito][token]',
          step: 'token_response_missing',
          hasPayload: !!payload,
          hasAccessToken: payload ? 'access_token' in payload : false,
          hasIdToken: payload ? 'id_token' in payload : false,
        })
      )
      .catch(() => {})
    throw new Error('Cognito token response was missing an access or ID token')
  }

  console.info('[cognito][token] requestTokenGrant: success', {
    hasAccessToken: !!payload.access_token,
    accessTokenPrefix: payload.access_token.substring(0, 16) + '...',
    hasIdToken: !!payload.id_token,
    idTokenPrefix: payload.id_token.substring(0, 16) + '...',
    hasRefreshToken: !!payload.refresh_token,
    tokenType: payload.token_type,
    expiresIn: payload.expires_in,
  })
  getPostHogClient()
    .then((ph) =>
      ph.capture('google_oauth_debug', {
        prefix: '[cognito][token]',
        step: 'token_request_success',
        hasAccessToken: !!payload.access_token,
        hasIdToken: !!payload.id_token,
        hasRefreshToken: !!payload.refresh_token,
        tokenType: payload.token_type,
        expiresIn: payload.expires_in,
      })
    )
    .catch(() => {})

  return payload
}

export async function saveStoredTokens(tokens: StoredTokens): Promise<StoredTokens> {
  const username = normalizeStoredUsername(tokens.username)
  await Promise.all([
    storage.set(KEYS.accessToken, tokens.accessToken),
    storage.set(KEYS.idToken, tokens.idToken),
    storage.set(KEYS.refreshToken, tokens.refreshToken),
    storage.set(KEYS.authMethod, tokens.authMethod),
    username ? storage.set(KEYS.username, username) : storage.remove(KEYS.username),
  ])

  return {
    ...tokens,
    username,
  }
}

async function saveTokenGrantTokens(params: {
  accessToken: string
  idToken: string
  refreshToken: string
  authMethod: AuthMethod
}): Promise<StoredTokens> {
  const profile = getTokenProfile(params.idToken, params.accessToken)
  return saveStoredTokens({
    accessToken: params.accessToken,
    idToken: params.idToken,
    refreshToken: params.refreshToken,
    username: profile.username,
    authMethod: params.authMethod,
  })
}

async function saveE2ETokens(email: string): Promise<StoredTokens> {
  const normalizedEmail = email.toLowerCase()
  const sub = sanitizeEmailToSubject(normalizedEmail)
  const accessToken = createE2EJwt({ sub, email: normalizedEmail, 'cognito:groups': [] })
  const idToken = createE2EJwt({ sub, email: normalizedEmail })
  const refreshToken = `e2e-refresh-${sub}`

  return saveStoredTokens({
    accessToken,
    idToken,
    refreshToken,
    username: normalizedEmail,
    authMethod: 'e2e',
  })
}

async function saveTokens(session: CognitoUserSession, username: string): Promise<StoredTokens> {
  return saveStoredTokens({
    accessToken: session.getAccessToken().getJwtToken(),
    idToken: session.getIdToken().getJwtToken(),
    refreshToken: session.getRefreshToken().getToken(),
    username,
    authMethod: 'password',
  })
}

export async function saveOAuthTokens(params: {
  accessToken: string
  idToken: string
  refreshToken: string
  authMethod?: Exclude<AuthMethod, 'password' | 'e2e'>
}): Promise<StoredTokens> {
  console.info('[cognito][token] saveOAuthTokens: saving tokens', {
    authMethod: params.authMethod ?? 'google',
    hasAccessToken: !!params.accessToken,
    hasIdToken: !!params.idToken,
    hasRefreshToken: !!params.refreshToken,
  })
  getPostHogClient()
    .then((ph) =>
      ph.capture('google_oauth_debug', {
        prefix: '[cognito][token]',
        step: 'oauth_tokens_saving',
        authMethod: params.authMethod ?? 'google',
        hasAccessToken: !!params.accessToken,
        hasIdToken: !!params.idToken,
        hasRefreshToken: !!params.refreshToken,
      })
    )
    .catch(() => {})
  return saveTokenGrantTokens({
    accessToken: params.accessToken,
    idToken: params.idToken,
    refreshToken: params.refreshToken,
    authMethod: params.authMethod ?? 'google',
  })
}

export async function exchangeAuthorizationCodeForTokens(params: {
  code: string
  codeVerifier: string
  redirectUri: string
  authMethod?: Exclude<AuthMethod, 'password' | 'e2e'>
}): Promise<StoredTokens> {
  console.info('[cognito][token] exchangeAuthorizationCodeForTokens: start', {
    codePrefix: params.code.substring(0, 8) + '...',
    codeVerifierLength: params.codeVerifier.length,
    redirectUri: params.redirectUri,
    authMethod: params.authMethod ?? 'google',
  })
  getPostHogClient()
    .then((ph) =>
      ph.capture('google_oauth_debug', {
        prefix: '[cognito][token]',
        step: 'code_exchange_start',
        codeVerifierLength: params.codeVerifier.length,
        redirectUri: params.redirectUri,
        authMethod: params.authMethod ?? 'google',
      })
    )
    .catch(() => {})

  const payload = await requestTokenGrant(
    new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: CLIENT_ID,
      code: params.code,
      code_verifier: params.codeVerifier,
      redirect_uri: params.redirectUri,
    })
  )

  if (!payload.refresh_token) {
    console.error(
      '[cognito][token] exchangeAuthorizationCodeForTokens: response missing refresh_token'
    )
    throw new Error('Cognito token response was missing a refresh token')
  }

  console.info('[cognito][token] exchangeAuthorizationCodeForTokens: success, saving tokens')
  getPostHogClient()
    .then((ph) =>
      ph.capture('google_oauth_debug', {
        prefix: '[cognito][token]',
        step: 'code_exchange_success',
      })
    )
    .catch(() => {})
  return saveOAuthTokens({
    accessToken: payload.access_token,
    idToken: payload.id_token,
    refreshToken: payload.refresh_token,
    authMethod: params.authMethod,
  })
}

export async function getStoredTokens(): Promise<StoredTokens | null> {
  const [accessToken, idToken, refreshToken, username, authMethodRaw] = await Promise.all([
    storage.get(KEYS.accessToken),
    storage.get(KEYS.idToken),
    storage.get(KEYS.refreshToken),
    storage.get(KEYS.username),
    storage.get(KEYS.authMethod),
  ])

  if (!accessToken || !idToken || !refreshToken) return null

  const authMethod: AuthMethod =
    authMethodRaw === 'google' || authMethodRaw === 'e2e' || authMethodRaw === 'password'
      ? authMethodRaw
      : 'password'
  const profile = getTokenProfile(idToken, accessToken)

  return {
    accessToken,
    idToken,
    refreshToken,
    username: normalizeStoredUsername(username) ?? profile.username,
    authMethod,
  }
}

export async function clearStoredTokens(): Promise<void> {
  await Promise.all(Object.values(KEYS).map((key) => storage.remove(key)))
}

// Deduplicates concurrent refresh calls so only one network round-trip is
// made even if multiple in-flight requests all receive a 401 simultaneously.
let _refreshPromise: Promise<StoredTokens> | null = null

export async function refreshStoredTokens(): Promise<StoredTokens> {
  if (_refreshPromise) return _refreshPromise
  _refreshPromise = _doRefresh().finally(() => {
    _refreshPromise = null
  })
  return _refreshPromise
}

async function doE2ERefresh(): Promise<StoredTokens> {
  const existing = await getStoredTokens()
  if (!existing) throw new Error('No stored tokens to refresh')
  if (!existing.username) throw new Error('No stored username to refresh')
  return saveE2ETokens(existing.username)
}

async function doOAuthRefresh(existing: StoredTokens): Promise<StoredTokens> {
  console.info('[cognito][token] doOAuthRefresh: using OAuth refresh_token grant', {
    authMethod: existing.authMethod,
    clientIdPrefix: CLIENT_ID.substring(0, 8) + '...',
  })
  getPostHogClient()
    .then((ph) =>
      ph.capture('google_oauth_debug', {
        prefix: '[cognito][token]',
        step: 'oauth_refresh_start',
        authMethod: existing.authMethod,
      })
    )
    .catch(() => {})
  const payload = await requestTokenGrant(
    new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: CLIENT_ID,
      refresh_token: existing.refreshToken,
    })
  )

  console.info('[cognito][token] doOAuthRefresh: OAuth refresh success, saving tokens')
  getPostHogClient()
    .then((ph) =>
      ph.capture('google_oauth_debug', {
        prefix: '[cognito][token]',
        step: 'oauth_refresh_success',
      })
    )
    .catch(() => {})
  return saveTokenGrantTokens({
    accessToken: payload.access_token,
    idToken: payload.id_token,
    refreshToken: payload.refresh_token ?? existing.refreshToken,
    authMethod: existing.authMethod,
  })
}

function doCognitoSDKRefresh(existing: StoredTokens, username: string): Promise<StoredTokens> {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({
      Username: username,
      Pool: pool,
      Storage: cognitoStorage,
    })
    const token = new CognitoRefreshToken({ RefreshToken: existing.refreshToken })
    user.refreshSession(token, async (err, session: CognitoUserSession) => {
      if (err) {
        reject(err)
        return
      }
      const tokens = await saveTokens(session, username)
      resolve(tokens)
    })
  })
}

function logOAuthRefreshError(refreshErr: unknown): void {
  const errMsg = refreshErr instanceof Error ? refreshErr.message : String(refreshErr)
  const errName = refreshErr instanceof Error ? refreshErr.name : 'Unknown'
  console.error('[cognito][token] _doRefresh: OAuth refresh failed', {
    error: errMsg,
    errorName: errName,
  })
  getPostHogClient()
    .then((ph) =>
      ph.capture('google_oauth_debug', {
        prefix: '[cognito][token]',
        step: 'oauth_refresh_failed',
        error: errMsg,
        errorName: errName,
      })
    )
    .catch(() => {})
}

async function _doRefresh(): Promise<StoredTokens> {
  console.info('[cognito][token] _doRefresh: starting token refresh')
  getPostHogClient()
    .then((ph) =>
      ph.capture('google_oauth_debug', {
        prefix: '[cognito][token]',
        step: 'refresh_starting',
      })
    )
    .catch(() => {})

  if (E2E_MOCK_AUTH) {
    return doE2ERefresh()
  }

  const existing = await getStoredTokens()
  if (!existing) {
    console.error('[cognito][token] _doRefresh: no stored tokens found')
    getPostHogClient()
      .then((ph) =>
        ph.capture('google_oauth_debug', {
          prefix: '[cognito][token]',
          step: 'refresh_no_tokens',
        })
      )
      .catch(() => {})
    throw new Error('No stored tokens to refresh')
  }

  const tokenPrefix = existing.refreshToken?.substring(0, 8) + '...' ?? 'none'
  console.info('[cognito][token] _doRefresh: found existing tokens', {
    authMethod: existing.authMethod,
    hasUsername: !!existing.username,
    hasRefreshToken: !!existing.refreshToken,
    refreshTokenPrefix: tokenPrefix,
  })
  getPostHogClient()
    .then((ph) =>
      ph.capture('google_oauth_debug', {
        prefix: '[cognito][token]',
        step: 'refresh_found_tokens',
        authMethod: existing.authMethod,
        hasUsername: !!existing.username,
        hasRefreshToken: !!existing.refreshToken,
      })
    )
    .catch(() => {})

  const useOAuth = existing.authMethod === 'google' || !existing.username
  if (useOAuth) {
    try {
      return await doOAuthRefresh(existing)
    } catch (refreshErr) {
      logOAuthRefreshError(refreshErr)
      throw refreshErr
    }
  }

  const username = existing.username
  if (!username) throw new Error('No stored username to refresh')

  return doCognitoSDKRefresh(existing, username)
}

export function cognitoSignUp(email: string, password: string): Promise<void> {
  if (E2E_MOCK_AUTH) {
    return (async () => {
      const normalizedEmail = email.trim().toLowerCase()
      const users = await loadE2EUsers()
      if (users[normalizedEmail]) {
        throw createE2EAuthError('UsernameExistsException', 'User already exists')
      }
      users[normalizedEmail] = {
        password,
        confirmed: false,
      }
      await saveE2EUsers(users)
    })()
  }

  return new Promise((resolve, reject) => {
    const attrs = [new CognitoUserAttribute({ Name: 'email', Value: email })]
    pool.signUp(email, password, attrs, [], (err) => {
      if (err) {
        reject(err)
      } else {
        resolve()
      }
    })
  })
}

export function cognitoSignIn(email: string, password: string): Promise<StoredTokens> {
  if (E2E_MOCK_AUTH) {
    return (async () => {
      if (readSignInFailureFlag()) {
        consumeSignInFailureFlag()
        throw createE2EAuthError('NotAuthorizedException', 'Incorrect username or password')
      }

      const normalizedEmail = email.trim().toLowerCase()
      const users = await loadE2EUsers()
      const user = users[normalizedEmail]
      if (!user) {
        throw createE2EAuthError('UserNotFoundException', 'User does not exist')
      }
      if (!user.confirmed) {
        throw createE2EAuthError('UserNotConfirmedException', 'User is not confirmed')
      }
      if (user.password !== password) {
        throw createE2EAuthError('NotAuthorizedException', 'Incorrect username or password')
      }
      return saveE2ETokens(normalizedEmail)
    })()
  }

  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: pool, Storage: cognitoStorage })
    // USER_PASSWORD_AUTH skips the expensive client-side SRP big-number math.
    // On mobile React Native lacks native crypto, making SRP much slower.
    if (Platform.OS !== 'web') {
      user.setAuthenticationFlowType('USER_PASSWORD_AUTH')
    }
    const authDetails = new AuthenticationDetails({ Username: email, Password: password })

    user.authenticateUser(authDetails, {
      onSuccess: async (session) => {
        const tokens = await saveTokens(session, email)
        resolve(tokens)
      },
      onFailure: (err) => {
        reject(err)
      },
      totpRequired: () => {
        reject(new Error('Login failed. Check your details and try again.'))
      },
      newPasswordRequired: () => {
        reject(new Error('New password required'))
      },
    })
  })
}

export async function cognitoSignOut(): Promise<void> {
  if (E2E_MOCK_AUTH) {
    await clearStoredTokens()
    return
  }

  const user = pool.getCurrentUser()
  user?.signOut()
  await clearStoredTokens()
}

export type MfaChallenge = {
  submitCode: (code: string) => Promise<StoredTokens>
}

export type AdminSignInResult =
  { type: 'tokens'; tokens: StoredTokens } | { type: 'mfa'; challenge: MfaChallenge }

export function cognitoAdminSignIn(email: string, password: string): Promise<AdminSignInResult> {
  if (E2E_MOCK_AUTH) {
    return (async () => {
      const tokens = await cognitoSignIn(email, password)
      return { type: 'tokens', tokens } as AdminSignInResult
    })()
  }

  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: pool, Storage: cognitoStorage })
    if (Platform.OS !== 'web') {
      user.setAuthenticationFlowType('USER_PASSWORD_AUTH')
    }
    const authDetails = new AuthenticationDetails({ Username: email, Password: password })

    user.authenticateUser(authDetails, {
      onSuccess: async (session) => {
        const tokens = await saveTokens(session, email)
        resolve({ type: 'tokens', tokens })
      },
      onFailure: (err) => {
        reject(err)
      },
      totpRequired: () => {
        const challenge: MfaChallenge = {
          submitCode: (code: string) =>
            new Promise((res, rej) => {
              user.sendMFACode(
                code,
                {
                  onSuccess: async (session) => {
                    const tokens = await saveTokens(session, email)
                    res(tokens)
                  },
                  onFailure: (err) => rej(err),
                },
                'SOFTWARE_TOKEN_MFA'
              )
            }),
        }
        resolve({ type: 'mfa', challenge })
      },
      newPasswordRequired: () => {
        reject(new Error('New password required'))
      },
    })
  })
}

export function confirmSignUp(email: string, code: string): Promise<void> {
  if (E2E_MOCK_AUTH) {
    return (async () => {
      const normalizedEmail = email.trim().toLowerCase()
      const users = await loadE2EUsers()
      const user = users[normalizedEmail]
      if (!user) {
        throw createE2EAuthError('UserNotFoundException', 'User does not exist')
      }
      if (code.trim() !== '123456') {
        throw createE2EAuthError('CodeMismatchException', 'Invalid confirmation code')
      }
      users[normalizedEmail] = {
        ...user,
        confirmed: true,
      }
      await saveE2EUsers(users)
    })()
  }

  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: pool, Storage: cognitoStorage })
    user.confirmRegistration(code, true, (err) => {
      if (err) {
        reject(err)
      } else {
        resolve()
      }
    })
  })
}

export function resendConfirmationCode(email: string): Promise<void> {
  if (E2E_MOCK_AUTH) {
    return (async () => {
      const normalizedEmail = email.trim().toLowerCase()
      const users = await loadE2EUsers()
      if (!users[normalizedEmail]) {
        throw createE2EAuthError('UserNotFoundException', 'User does not exist')
      }
    })()
  }

  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: pool, Storage: cognitoStorage })
    user.resendConfirmationCode((err) => {
      if (err) reject(err)
      else resolve()
    })
  })
}

export function forgotPassword(email: string): Promise<void> {
  if (E2E_MOCK_AUTH) {
    return (async () => {
      const normalizedEmail = email.trim().toLowerCase()
      const users = await loadE2EUsers()
      if (!users[normalizedEmail]) {
        throw createE2EAuthError('UserNotFoundException', 'User does not exist')
      }
    })()
  }

  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: pool, Storage: cognitoStorage })
    user.forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    })
  })
}

export function confirmForgotPassword(
  email: string,
  code: string,
  newPassword: string
): Promise<void> {
  if (E2E_MOCK_AUTH) {
    return (async () => {
      const normalizedEmail = email.trim().toLowerCase()
      if (!code.trim()) {
        throw createE2EAuthError('CodeMismatchException', 'Invalid confirmation code')
      }
      const users = await loadE2EUsers()
      const user = users[normalizedEmail]
      if (!user) {
        throw createE2EAuthError('UserNotFoundException', 'User does not exist')
      }
      users[normalizedEmail] = {
        ...user,
        password: newPassword,
        confirmed: true,
      }
      await saveE2EUsers(users)
    })()
  }

  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: pool, Storage: cognitoStorage })
    user.confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    })
  })
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  if (E2E_MOCK_AUTH) {
    const stored = await getStoredTokens()
    if (!stored || !stored.username) throw new Error('Not authenticated')
    const users = await loadE2EUsers()
    const user = users[stored.username]
    if (!user || user.password !== currentPassword) {
      throw createE2EAuthError('NotAuthorizedException', 'Incorrect username or password')
    }
    users[stored.username] = {
      ...user,
      password: newPassword,
      confirmed: true,
    }
    await saveE2EUsers(users)
    return
  }

  const stored = await getStoredTokens()
  if (!stored) throw new Error('Not authenticated')
  if (stored.authMethod !== 'password') {
    throw new Error('Password changes are only available for email sign-ins.')
  }
  if (!stored.username) throw new Error('Not authenticated')
  const username = stored.username

  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: username, Pool: pool, Storage: cognitoStorage })
    user.setSignInUserSession(
      new CognitoUserSession({
        IdToken: new CognitoIdToken({ IdToken: stored.idToken }),
        AccessToken: new CognitoAccessToken({ AccessToken: stored.accessToken }),
        RefreshToken: new CognitoRefreshToken({ RefreshToken: stored.refreshToken }),
      })
    )
    user.changePassword(currentPassword, newPassword, (err) => {
      if (err) reject(err)
      else resolve()
    })
  })
}
